import { Link } from "react-router-dom";
import { domainOf, timeAgo, slugify, similarQuery, outboundHref } from "../lib/utils-tv";
import { recordLaunch } from "../lib/api";

export default function ToolItem({ index, tool, voted, onUpvote }) {
  const price = tool.pricing || "$?";
  return (
    <li
      id={`tool-${tool.id}`}
      className="flex items-start gap-3 py-3 border-b border-[#eee] tv-tool-row"
      data-testid={`tool-row-${tool.id}`}
    >
      <div className="flex flex-col items-center w-10 select-none pt-0.5">
        <span className="text-[11px] text-[#888]">{String(index).padStart(2, "0")}.</span>
        <button
          type="button"
          aria-label="upvote"
          className={`tv-upvote mt-1 ${voted ? "voted" : ""}`}
          onClick={() => onUpvote(tool.id)}
          data-testid={`upvote-btn-${tool.id}`}
          title={voted ? "Click to un-vote" : "Upvote"}
        >
          ▲
        </button>
        <span className="text-[11px] mt-1 tabular-nums" data-testid={`upvote-count-${tool.id}`}>
          {tool.upvotes}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3 flex-wrap">
          {tool.vendor?.logo && (
            <img
              src={tool.vendor.logo}
              alt=""
              loading="lazy"
              className="w-[18px] h-[18px] object-contain border border-black bg-white p-[1px] self-center shrink-0"
              data-testid={`tool-logo-${tool.id}`}
              title={`${tool.title} — Starter listing`}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          {tool.slug ? (
            <Link
              to={`/tool/${tool.slug}`}
              className="tv-link text-base font-semibold break-words"
              data-testid={`tool-title-${tool.id}`}
            >
              {tool.title}
            </Link>
          ) : (
            <a
              href={outboundHref(tool)}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={() => recordLaunch(tool.id)}
              className="tv-link text-base font-semibold break-words"
              data-testid={`tool-title-${tool.id}`}
            >
              {tool.title}
            </a>
          )}
          <a
            href={outboundHref(tool, "home")}
            target="_blank"
            rel="sponsored noopener noreferrer"
            onClick={() => recordLaunch(tool.id)}
            className="text-[12px] text-[#888] hover:text-black hover:underline"
            data-testid={`tool-domain-${tool.id}`}
            title={`Visit ${domainOf(tool.url)} ↗`}
          >
            ({domainOf(tool.url)} ↗)
          </a>
          <Link
            to="/about#pricing"
            className="ml-auto text-[12px] font-bold text-[#000] tabular-nums whitespace-nowrap hover:bg-black hover:text-white no-underline"
            title={`Pricing: ${price}${tool.pricing_model ? ` · ${tool.pricing_model.replace("_", " ")}` : ""} — click for tier legend`}
            data-testid={`tool-price-${tool.id}`}
          >
            {price}
            {tool.pricing_model && tool.pricing_model !== "subscription" && (
              <span className="ml-1 text-[10px] font-normal text-[#666]" data-testid={`tool-pricing-model-${tool.id}`}>
                {/* subscription is the default model for SaaS — would just
                    be noise on every row. Only surface the non-default
                    commitment shapes (one-time, usage, freemium, free,
                    enterprise) so the eye spots them instantly. */}
                · {tool.pricing_model.replace("_", " ")}
              </span>
            )}
          </Link>
        </div>
        <div className="text-[13px] text-[#222] mt-0.5">{tool.description}</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-[#666]">
          {/* [key] feature row — always own line, above the meta tags.
              Inline `text-[11px] text-[#555]` mimics the .tv-tag look
              without inheriting its `white-space: nowrap` which would
              cause horizontal page overflow on mobile for long feature
              copy. break-words handles unbreakable tokens (long URLs etc). */}
          <span className="text-[11px] text-[#555] whitespace-normal break-words basis-full">
            <span className="text-[#000]">[key]</span>{" "}
            {(() => {
              const q = similarQuery(tool.key_feature);
              return q ? (
                <Link
                  to={`/?q=${encodeURIComponent(q)}`}
                  className="tv-link"
                  data-testid={`tool-key-${tool.id}`}
                  title={`Find tools related to "${q}"`}
                >
                  {tool.key_feature}
                </Link>
              ) : (
                tool.key_feature
              );
            })()}
          </span>
          {tool.affiliated && (
            <>
              <Link
                to="/terms#affiliate-disclosure"
                className="tv-tag text-[#000] hover:underline"
                title="This listing is operated by or affiliated with TerminalValue.io — see disclosure"
                data-testid={`affiliated-${tool.id}`}
              >
                <span className="text-[#000]">[affiliated]</span>
              </Link>
              <span className="text-[#bbb]">·</span>
            </>
          )}
          {tool.asset_class && (
            <>
              <span className="tv-tag">
                <span className="text-[#000]">[asset]</span>{" "}
                <Link
                  to={`/c/${slugify(tool.asset_class)}`}
                  className="tv-link"
                  data-testid={`tool-asset-${tool.id}`}
                  title={`See all ${tool.asset_class} tools`}
                >
                  {tool.asset_class}
                </Link>
              </span>
              <span className="text-[#bbb]">·</span>
            </>
          )}
          <span className="tv-tag">
            <span className="text-[#000]">[function]</span>{" "}
            {(() => {
              // Show only the primary tag. The rest stay in the DB and
              // power /tag/ landing pages, similarity, search filters
              // and SEO — but the row stays clean and scannable.
              const tagList =
                Array.isArray(tool.tags) && tool.tags.length > 0
                  ? tool.tags
                  : [tool.category, tool.category_2].filter(Boolean);
              const primary = tagList[0];
              if (!primary) return null;
              return (
                <Link
                  to={`/tag/${slugify(primary)}`}
                  className="tv-link"
                  data-testid={`tool-tag-${tool.id}-${slugify(primary)}`}
                  title={`See all ${primary} tools`}
                >
                  {primary}
                </Link>
              );
            })()}
          </span>
          {tool.is_terminal && Array.isArray(tool.terminal_types) && tool.terminal_types.length > 0 && (
            <>
              <span className="text-[#bbb]">·</span>
              <span className="tv-tag" data-testid={`tool-terminal-types-${tool.id}`}>
                <span className="text-[#000]">[terminal]</span>{" "}
                {tool.terminal_types.map((ft, i) => (
                  <span key={ft}>
                    <Link
                      to={`/?terminals=1&terminal_type=${encodeURIComponent(ft)}`}
                      className="tv-link"
                      title={`See all ${ft} terminals`}
                    >
                      {ft}
                    </Link>
                    {i < tool.terminal_types.length - 1 && <span className="text-[#bbb]">, </span>}
                  </span>
                ))}
              </span>
            </>
          )}
          <span className="text-[#bbb]">·</span>
          <span className="tv-tag" title={tool.submitted_at}>added {timeAgo(tool.approved_at || tool.submitted_at)}</span>
          {/* [via] chip — surfaced ONLY when delivery is non-default.
              Plain ["web"] tools (most SaaS) don't get a chip — would be
              noise. Anything else (claude_skill, self_host, api, mobile,
              etc.) gets called out so the eye spots non-clickable /
              non-standard delivery instantly. Not a link — informational
              only. Use sort=[via] to group all same-delivery tools. */}
          {(() => {
            const dl = Array.isArray(tool.delivery) ? tool.delivery : [];
            const nonDefault = dl.filter((d) => d !== "web");
            if (nonDefault.length === 0) return null;
            const label = nonDefault
              .map((d) => {
                if (d === "chatgpt_gpt") return "GPT";
                if (d === "self_host") return "self-host";
                return d.replace(/_/g, " ");
              })
              .join(", ");
            return (
              <>
                <span className="text-[#bbb]">·</span>
                <span
                  className="tv-tag"
                  data-testid={`tool-delivery-${tool.id}`}
                  title={`Delivered via ${label}`}
                >
                  <span className="text-[#000]">[via]</span> {label}
                </span>
              </>
            );
          })()}
          {tool.is_open_source && (
            <>
              <span className="text-[#bbb]">·</span>
              <span
                className="tv-tag"
                data-testid={`tool-open-source-${tool.id}`}
                title="Open source — source is public / self-hostable"
              >
                <span className="text-[#000]">[open source]</span>
              </span>
            </>
          )}
          {tool.builder === "indie" && (
            <>
              <span className="text-[#bbb]">·</span>
              <span
                className="tv-tag"
                data-testid={`tool-builder-${tool.id}`}
                title="Indie / solo / hobby project — no incorporated business behind it"
              >
                <span className="text-[#000]">[indie]</span>
              </span>
            </>
          )}
          {Array.isArray(tool.tickers) && tool.tickers.length > 0 && (
            <>
              <span className="text-[#bbb]">·</span>
              <span
                className="tv-tag"
                data-testid={`tool-tickers-${tool.id}`}
                title={
                  tool.tickers.length === 1
                    ? `Dedicated to $${tool.tickers[0]}`
                    : `Covers ${tool.tickers.length} tickers`
                }
              >
                <span className="text-[#000]">[ticker]</span>{" "}
                {tool.tickers.map((tk, i) => (
                  <span key={tk}>
                    <Link
                      to={`/t/${tk}`}
                      className="tv-link"
                      data-testid={`tool-ticker-${tool.id}-${tk}`}
                    >
                      ${tk}
                    </Link>
                    {i < tool.tickers.length - 1 && <span className="text-[#bbb]">,</span>}
                    {i < tool.tickers.length - 1 && " "}
                  </span>
                ))}
              </span>
            </>
          )}
          {/* [similar] removed from the row — it cluttered the home
              listing. The full <RelatedTools /> section already lives
              on the tool detail page, which is the right place for
              users who've already committed to looking deeper. */}
        </div>
      </div>
    </li>
  );
}
